import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import { exitSuggestion, type SuggestionKeyDownProps, type SuggestionProps } from "@tiptap/suggestion"
import { PluginKey } from "@tiptap/pm/state"

import {
  createReplyEditorExtensions,
  hasUnsupportedReplyMarkdown,
  type MentionUser,
  type ReplyMentionSuggestion,
} from "../reply_composer/markdown"

const MENTION_DEBOUNCE_MS = 150

export default class ReplyComposerController extends Controller {
  static targets = ["editor", "textarea", "richToolbar", "sourceToggle", "suggestions", "unsupportedNotice"]

  static values = {
    mentionsUrl: String,
    linkTextPrompt: String,
    linkUrlPrompt: String,
    sourceLabel: String,
    richTextLabel: String,
    mentionSuggestionsLabel: String,
    noMentionMatches: String,
    mentionLookupFailed: String,
    unsupportedMarkdownMessage: String,
  }

  declare readonly editorTarget: HTMLElement
  declare readonly textareaTarget: HTMLTextAreaElement
  declare readonly richToolbarTarget: HTMLElement
  declare readonly sourceToggleTarget: HTMLButtonElement
  declare readonly suggestionsTarget: HTMLElement
  declare readonly unsupportedNoticeTarget: HTMLElement
  declare mentionsUrlValue: string
  declare linkTextPromptValue: string
  declare linkUrlPromptValue: string
  declare sourceLabelValue: string
  declare richTextLabelValue: string
  declare mentionSuggestionsLabelValue: string
  declare noMentionMatchesValue: string
  declare mentionLookupFailedValue: string
  declare unsupportedMarkdownMessageValue: string

  private editor?: Editor
  private form?: HTMLFormElement
  private abortController?: AbortController
  private mentionRequestId = 0
  private suggestionProps?: SuggestionProps<MentionUser, MentionUser>
  private readonly mentionPluginKey = new PluginKey("reply-composer-mentions")
  private selectedSuggestionIndex = 0
  private composing = false
  private initializing = true
  private richEdited = false
  private sourceMode = false
  private lookupFailed = false
  private suggestionViewportTracking = false

  private readonly handleSubmit = (): void => {
    if (this.richEdited && !this.sourceMode) this.syncTextarea()
  }

  private readonly handleBeforeCache = (): void => {
    this.prepareForRemoval()
  }

  private readonly handleCompositionStart = (): void => {
    this.composing = true
  }

  private readonly handleCompositionEnd = (): void => {
    window.setTimeout(() => {
      this.composing = false
    }, 0)
  }

  private readonly handleSuggestionViewportChange = (): void => {
    if (this.suggestionProps) this.positionSuggestions(this.suggestionProps)
  }

  connect(): void {
    this.form = this.element.closest("form") ?? undefined
    this.form?.addEventListener("submit", this.handleSubmit)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
    this.editorTarget.addEventListener("compositionstart", this.handleCompositionStart)
    this.editorTarget.addEventListener("compositionend", this.handleCompositionEnd)

    this.sourceMode = hasUnsupportedReplyMarkdown(this.textareaTarget.value)
    this.editor = new Editor({
      element: this.editorTarget,
      extensions: createReplyEditorExtensions(this.mentionSuggestion()),
      content: this.textareaTarget.value,
      contentType: "markdown",
      editorProps: {
        attributes: {
          class: "reply-composer__prosemirror",
          role: "textbox",
          "aria-multiline": "true",
          "aria-autocomplete": "list",
          "aria-haspopup": "listbox",
          "aria-label": this.textareaTarget.getAttribute("aria-label") ?? "",
        },
      },
      onUpdate: () => {
        if (this.initializing) return
        this.richEdited = true
        this.syncTextarea()
      },
    })
    this.initializing = false
    this.sourceToggleTarget.hidden = false
    this.setEditorSuggestionState(false)
    this.updateMode()
  }

  disconnect(): void {
    this.prepareForRemoval()
  }

  toggleSource(): void {
    if (!this.editor) return

    if (this.sourceMode) {
      if (hasUnsupportedReplyMarkdown(this.textareaTarget.value)) {
        this.unsupportedNoticeTarget.hidden = false
        this.unsupportedNoticeTarget.textContent = this.unsupportedMarkdownMessageValue
        this.textareaTarget.focus()
        return
      }

      this.editor.commands.setContent(this.textareaTarget.value, {
        contentType: "markdown",
        emitUpdate: false,
      })
      this.sourceMode = false
      this.updateMode()
      this.editor.commands.focus()
      return
    }

    if (this.richEdited) this.syncTextarea()
    this.sourceMode = true
    this.updateMode()
    this.textareaTarget.focus()
  }

  bold(): void {
    this.editor?.chain().focus().toggleBold().run()
  }

  italic(): void {
    this.editor?.chain().focus().toggleItalic().run()
  }

  heading(): void {
    this.editor?.chain().focus().toggleHeading({ level: 2 }).run()
  }

  bulletList(): void {
    this.editor?.chain().focus().toggleBulletList().run()
  }

  orderedList(): void {
    this.editor?.chain().focus().toggleOrderedList().run()
  }

  quote(): void {
    this.editor?.chain().focus().toggleBlockquote().run()
  }

  code(): void {
    this.editor?.chain().focus().toggleCode().run()
  }

  link(): void {
    if (!this.editor) return

    const text = window.prompt(this.linkTextPromptValue)?.trim()
    if (!text) return

    const url = window.prompt(this.linkUrlPromptValue)?.trim()
    if (!url) return

    const { from, to } = this.editor.state.selection
    if (from !== to) {
      this.editor.chain().focus().setLink({ href: url }).run()
    } else {
      this.editor.chain().focus().insertContent(`[${text}](${url})`, { contentType: "markdown" }).run()
    }
  }

  private mentionSuggestion(): ReplyMentionSuggestion {
    return {
      char: "@",
      pluginKey: this.mentionPluginKey,
      allowSpaces: true,
      items: ({ query }) => this.loadMentionUsers(query),
      render: () => ({
        onStart: (props) => {
          this.selectedSuggestionIndex = 0
          this.lookupFailed = false
          this.suggestionProps = props
          this.renderSuggestions(props)
        },
        onUpdate: (props) => {
          this.suggestionProps = props
          this.selectedSuggestionIndex = Math.min(
            this.selectedSuggestionIndex,
            Math.max(0, props.items.length - 1),
          )
          this.renderSuggestions(props)
        },
        onExit: () => {
          this.clearSuggestionView()
        },
        onKeyDown: (props) => this.handleSuggestionKeyDown(props),
      }),
    }
  }

  private async loadMentionUsers(query: string): Promise<MentionUser[]> {
    this.abortController?.abort()

    const requestId = ++this.mentionRequestId
    const controller = new AbortController()
    this.abortController = controller

    const debounceCompleted = await this.waitForMentionDebounce(controller.signal)

    if (!debounceCompleted || requestId !== this.mentionRequestId) return []

    try {
      const url = new URL(this.mentionsUrlValue, window.location.origin)
      url.searchParams.set("q", query)
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: controller.signal,
      })

      if (!response.ok) throw new Error(`mention lookup failed: ${response.status}`)

      const payload = (await response.json()) as { users?: unknown }
      if (requestId !== this.mentionRequestId) return []

      this.lookupFailed = false
      return this.validMentionUsers(payload.users)
    } catch {
      if (controller.signal.aborted || requestId !== this.mentionRequestId) return []
      this.lookupFailed = true
      return []
    }
  }

  private waitForMentionDebounce(signal: AbortSignal): Promise<boolean> {
    return new Promise((resolve) => {
      if (signal.aborted) {
        resolve(false)
        return
      }

      const timer = setTimeout(() => {
        signal.removeEventListener("abort", onAbort)
        resolve(true)
      }, MENTION_DEBOUNCE_MS)

      const onAbort = (): void => {
        clearTimeout(timer)
        resolve(false)
      }

      signal.addEventListener("abort", onAbort, { once: true })
    })
  }

  private validMentionUsers(value: unknown): MentionUser[] {
    if (!Array.isArray(value)) return []

    return value.flatMap((candidate) => {
      if (!candidate || typeof candidate !== "object") return []
      const user = candidate as { id?: unknown; label?: unknown }
      const id = String(user.id ?? "")
      const label = String(user.label ?? "")
      return /^[1-9]\d*$/.test(id) && label.trim() ? [{ id, label }] : []
    })
  }

  private handleSuggestionKeyDown({ event }: SuggestionKeyDownProps): boolean {
    if (this.composing || !this.suggestionProps) return false

    const items = this.suggestionProps.items
    if (!items.length) {
      if (event.key === "Escape") {
        event.preventDefault()
        this.closeSuggestions()
        return true
      }
      return false
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.selectedSuggestionIndex = (this.selectedSuggestionIndex + 1) % items.length
      this.renderSuggestions(this.suggestionProps)
      return true
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.selectedSuggestionIndex = (this.selectedSuggestionIndex - 1 + items.length) % items.length
      this.renderSuggestions(this.suggestionProps)
      return true
    }

    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault()
      this.suggestionProps.command(items[this.selectedSuggestionIndex])
      return true
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.closeSuggestions()
      return true
    }

    return false
  }

  private renderSuggestions(props: SuggestionProps<MentionUser, MentionUser>): void {
    this.suggestionsTarget.hidden = false
    this.trackSuggestionViewport()
    this.setEditorSuggestionState(true)
    this.suggestionsTarget.setAttribute("aria-label", this.mentionSuggestionsLabelValue)
    this.suggestionsTarget.replaceChildren()

    if (!props.items.length) {
      this.suggestionsTarget.textContent = this.lookupFailed
        ? this.mentionLookupFailedValue
        : this.noMentionMatchesValue
      this.positionSuggestions(props)
      return
    }

    props.items.forEach((item, index) => {
      const button = document.createElement("button")
      button.type = "button"
      button.role = "option"
      button.id = `${this.suggestionsTarget.id}-option-${item.id}`
      button.className = "reply-composer__suggestion"
      button.textContent = `@${item.label}`
      button.setAttribute("aria-selected", String(index === this.selectedSuggestionIndex))
      button.addEventListener("pointerdown", (event) => event.preventDefault())
      button.addEventListener("click", () => props.command(item))
      this.suggestionsTarget.append(button)
    })

    this.positionSuggestions(props)
    this.editorDom().setAttribute(
      "aria-activedescendant",
      `${this.suggestionsTarget.id}-option-${props.items[this.selectedSuggestionIndex].id}`,
    )
  }

  private positionSuggestions(props: SuggestionProps<MentionUser, MentionUser>): void {
    const updatePosition = (): void => {
      const rect = props.clientRect?.()
      if (!rect) return

      const width = Math.max(0, Math.min(360, window.innerWidth - 24))
      const margin = 12
      this.suggestionsTarget.style.width = `${width}px`
      this.suggestionsTarget.style.maxHeight = `${Math.max(0, window.innerHeight - margin * 2)}px`
      const menuHeight = this.suggestionsTarget.getBoundingClientRect().height
      const maxTop = Math.max(margin, window.innerHeight - menuHeight - margin)
      const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin))
      const belowTop = rect.bottom + 4
      const aboveTop = rect.top - menuHeight - 4
      const preferredTop = belowTop + menuHeight <= window.innerHeight - margin ? belowTop : aboveTop
      const top = Math.min(maxTop, Math.max(margin, preferredTop))

      this.suggestionsTarget.style.left = `${left}px`
      this.suggestionsTarget.style.top = `${top}px`
    }

    updatePosition()
    window.requestAnimationFrame(() => {
      if (this.suggestionProps === props && !this.suggestionsTarget.hidden) updatePosition()
    })
  }

  private updateMode(): void {
    this.editorTarget.hidden = this.sourceMode
    this.textareaTarget.hidden = !this.sourceMode
    this.richToolbarTarget.hidden = this.sourceMode
    this.unsupportedNoticeTarget.hidden = !hasUnsupportedReplyMarkdown(this.textareaTarget.value)
    this.unsupportedNoticeTarget.textContent = this.unsupportedMarkdownMessageValue
    this.sourceToggleTarget.textContent = this.sourceMode
      ? this.richTextLabelValue
      : this.sourceLabelValue
    this.sourceToggleTarget.setAttribute("aria-pressed", String(this.sourceMode))
  }

  private syncTextarea(): void {
    if (!this.editor) return
    this.textareaTarget.value = this.editor.getMarkdown()
  }

  private closeSuggestions(): void {
    this.clearSuggestionView()
    if (this.editor) exitSuggestion(this.editor.view, this.mentionPluginKey)
  }

  private clearSuggestionView(): void {
    this.suggestionProps = undefined
    this.untrackSuggestionViewport()
    this.suggestionsTarget.hidden = true
    this.suggestionsTarget.replaceChildren()
    this.setEditorSuggestionState(false)
  }

  private editorDom(): HTMLElement {
    return (this.editor?.view.dom as HTMLElement | undefined) ?? this.editorTarget
  }

  private setEditorSuggestionState(expanded: boolean): void {
    const editorDom = this.editorDom()
    editorDom.setAttribute("aria-controls", this.suggestionsTarget.id)
    editorDom.setAttribute("aria-expanded", String(expanded))
    if (!expanded) editorDom.removeAttribute("aria-activedescendant")
  }

  private trackSuggestionViewport(): void {
    if (this.suggestionViewportTracking) return

    window.addEventListener("resize", this.handleSuggestionViewportChange)
    window.addEventListener("scroll", this.handleSuggestionViewportChange, true)
    this.suggestionViewportTracking = true
  }

  private untrackSuggestionViewport(): void {
    if (!this.suggestionViewportTracking) return

    window.removeEventListener("resize", this.handleSuggestionViewportChange)
    window.removeEventListener("scroll", this.handleSuggestionViewportChange, true)
    this.suggestionViewportTracking = false
  }

  private prepareForRemoval(): void {
    if (this.richEdited && !this.sourceMode) this.syncTextarea()
    this.form?.removeEventListener("submit", this.handleSubmit)
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    this.editorTarget.removeEventListener("compositionstart", this.handleCompositionStart)
    this.editorTarget.removeEventListener("compositionend", this.handleCompositionEnd)
    this.abortController?.abort()
    this.clearSuggestionView()
    this.editor?.destroy()
    this.editor = undefined
  }
}
